import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getComicById } from '@/mocks/comics';
import type { Chapter } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const comic = id ? getComicById(parseInt(id, 10)) : undefined;

  if (!comic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap">返回首页</Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - yy漫画网韩漫免费在线阅读`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.date || comic.updateDate,
    dateModified: comic.updateDate,
  };

  return (
    <main className="min-h-screen bg-background-50 pt-16">
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: `${comic.category}韩漫`, href: `/category/${comic.categorySlug}` },
            { label: comic.title },
          ]}
        />

        {!selectedChapter ? (
          <article>
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="w-full lg:w-[280px] flex-shrink-0">
                <div className="rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - yy漫画网韩漫免费在线阅读`}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm text-foreground-600">
                    <i className="ri-user-line mr-1"></i> {comic.author}
                  </span>
                  <span className="text-sm text-foreground-500">|</span>
                  <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-primary-50 text-primary-600 whitespace-nowrap">
                    {comic.category}
                  </span>
                  <span className={`inline-block px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${
                    comic.status === '连载中' ? 'bg-accent-50 text-accent-600' : 'bg-secondary-50 text-secondary-600'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex items-center gap-5 mb-6 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <strong className="text-foreground-900">{comic.rating}</strong> 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    <strong className="text-foreground-900">{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}</strong> 阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-file-list-line"></i>
                    <strong className="text-foreground-900">{comic.chapters.length}</strong> 章节
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="inline-block px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-5 mb-8">
                  <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="mb-8">
                  <h2 className="text-lg font-bold font-heading text-foreground-900 mb-4">
                    章节<span className="text-primary-600">列表</span>
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[400px] overflow-y-auto pr-2">
                    {comic.chapters.map((chapter) => (
                      <button
                        key={chapter.id}
                        onClick={() => setSelectedChapter(chapter)}
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer whitespace-nowrap text-left"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-xs font-bold">
                            {chapter.number}
                          </span>
                          <div>
                            <div className="text-sm font-medium text-foreground-900">第{chapter.number}话</div>
                            <div className="text-xs text-foreground-500">{chapter.title}</div>
                          </div>
                        </div>
                        <div className="text-xs text-foreground-400 whitespace-nowrap">{chapter.date}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <button
                    onClick={() => {
                      const firstChapter = comic.chapters[0];
                      if (firstChapter) setSelectedChapter(firstChapter);
                    }}
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-circle-line"></i>
                    开始阅读
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-all duration-300 cursor-pointer whitespace-nowrap">
                    <i className="ri-bookmark-line"></i>
                    收藏漫画
                  </button>
                </div>
              </div>
            </div>
          </article>
        ) : (
          <article className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => setSelectedChapter(null)}
                className="inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 返回详情
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    const currentIndex = comic.chapters.findIndex((c) => c.id === selectedChapter.id);
                    if (currentIndex > 0) setSelectedChapter(comic.chapters[currentIndex - 1]);
                  }}
                  disabled={selectedChapter.number === 1}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <i className="ri-arrow-left-s-line"></i> 上一话
                </button>
                <span className="text-sm font-medium text-foreground-900">
                  第{selectedChapter.number}话
                </span>
                <button
                  onClick={() => {
                    const currentIndex = comic.chapters.findIndex((c) => c.id === selectedChapter.id);
                    if (currentIndex < comic.chapters.length - 1) setSelectedChapter(comic.chapters[currentIndex + 1]);
                  }}
                  disabled={selectedChapter.number === comic.chapters.length}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  下一话 <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>

            <div className="bg-background-50 rounded-lg border border-background-200/70 p-8 md:p-12">
              <h1 className="text-xl font-bold font-heading text-foreground-900 mb-2">
                第{selectedChapter.number}话：{selectedChapter.title}
              </h1>
              <p className="text-xs text-foreground-500 mb-8">更新日期：{selectedChapter.date}</p>
              <div className="prose prose-sm max-w-none">
                <div className="bg-background-100 rounded-lg p-6 mb-6">
                  <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                    {selectedChapter.content}
                  </p>
                </div>
                <div className="text-center py-12 border-2 border-dashed border-background-200 rounded-lg">
                  <i className="ri-image-line text-5xl text-foreground-300"></i>
                  <p className="text-sm text-foreground-400 mt-3">漫画图片内容加载区域</p>
                  <p className="text-xs text-foreground-400 mt-1">下拉式阅读体验，向下滑动查看更多</p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 mt-6">
                  <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                    故事还在继续...（更多精彩内容请持续关注yy漫画网，每日更新最新韩漫章节）
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-8">
              <button
                onClick={() => {
                  const currentIndex = comic.chapters.findIndex((c) => c.id === selectedChapter.id);
                  if (currentIndex > 0) setSelectedChapter(comic.chapters[currentIndex - 1]);
                }}
                disabled={selectedChapter.number === 1}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <i className="ri-arrow-left-line"></i> 上一话
              </button>
              <button
                onClick={() => setSelectedChapter(null)}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-list-unordered"></i> 章节目录
              </button>
              <button
                onClick={() => {
                  const currentIndex = comic.chapters.findIndex((c) => c.id === selectedChapter.id);
                  if (currentIndex < comic.chapters.length - 1) setSelectedChapter(comic.chapters[currentIndex + 1]);
                }}
                disabled={selectedChapter.number === comic.chapters.length}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                下一话 <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </article>
        )}
      </div>
    </main>
  );
}